import { graphHooks } from '@tunasong/graph-lib/react'
import type { SearchSummary } from '@tunasong/models'
import { isUrl } from '@tunasong/models'
import { clientApi, useThunkDispatch } from '@tunasong/redux'
import type { Entity, EntityLink, Link as LinkType, Persisted } from '@tunasong/schemas'
import { FullSearch } from '@tunasong/search'
import { Dialog, DialogTitle, TextField, Typography, VBox, Button, LinearProgress, DialogActions, DialogContent} from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { useState } from 'react'
import styles from './bookmark-dialog.module.css'

export interface BookmarkDialogProps {
  open: boolean
  parent: Persisted<Entity>
  type: 'internal' | 'external'
  onClose: () => void
}

export const BookmarkDialog: FC<BookmarkDialogProps> = props => {
  const { parent, type, onClose, open } = props

  const dispatch = useThunkDispatch()
  const { createEntity } = graphHooks.useEntityGraph()

  const [externalLink, setExternalLink] = useState('')
  const [linkedEntity, setLinkedEntity] = useState<SearchSummary>()

  const isValid = type === 'external' ? isUrl(externalLink) : Boolean(linkedEntity)

  const [isCreatingEntity, setIsCreatingEntity] = useState(false)

  const onSelected = (entity: SearchSummary) => {
    setLinkedEntity(entity)
  }

  const createLinkBookmark = async (ev: MouseEvent) => {
    if (isCreatingEntity) {
      return
    }
    ev.preventDefault()

    setIsCreatingEntity(true)

    // Check type of link
    if (type === 'internal' && linkedEntity) {
      const entity = {
        type: 'entitylink',
        name: linkedEntity.name,
        ref: {
          id: linkedEntity.id,
          type: linkedEntity.type,
          name: linkedEntity.name,
          description: linkedEntity.description,
        },
        tags: ['sys:resource'],
      } satisfies EntityLink

      await createEntity({
        parent,
        entity,
      }).finally(() => {
        setIsCreatingEntity(false)
        onClose()
      })
      return
    }
    if (externalLink) {
      const { title, description, icon } = await dispatch(
        clientApi.endpoints.getUrlPreview.initiate({ url: externalLink })
      ).unwrap()
      const entity = {
        type: 'link',
        name: title,
        url: externalLink,
        tags: ['sys:resource'],
        description,
        icon,
      } satisfies LinkType

      await createEntity({
        parent,
        entity,
      }).finally(() => {
        setIsCreatingEntity(false)
        onClose()
      })
      onClose()
      return
    }
  }
  const isBusy = isCreatingEntity

  return (
    <Dialog open={open} onClose={onClose} className={styles.dialog}>
      <DialogTitle onClose={onClose} title="Create new bookmark" />
      <DialogContent>
        {type === 'internal' ? (
          <FullSearch onOpen={onSelected} selected={linkedEntity} onClose={() => {}} />
        ) : null}

        {type === 'external' ? (
          <VBox>
            <Typography variant="subtitle1" className={styles.subtitle}>
              External link
            </Typography>
            <TextField label="URL" fullWidth value={externalLink} onChange={ev => setExternalLink(ev.target.value)} />
          </VBox>
        ) : null}
        {isCreatingEntity ? <LinearProgress /> : null}
      </DialogContent>
      <DialogActions>
        <Button disabled={isBusy} onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button disabled={!isValid || isBusy} onClick={createLinkBookmark} color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BookmarkDialog
